import { useState, useRef, useCallback, useEffect } from 'react';
import { Mp3Encoder } from '@/lib/lamejs-wrapper';

export interface MP3RecorderState {
  isRecording: boolean;
  isPaused: boolean;
  duration: number;
  audioBlob: Blob | null;
  error: string | null;
  isConverting: boolean;
}

export interface UseMP3RecorderReturn extends MP3RecorderState {
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<Blob | null>;
  cancelRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
}

// Convert Float32 samples to Int16 for MP3 encoding
function floatTo16BitPCM(float32Array: Float32Array): Int16Array {
  const int16Array = new Int16Array(float32Array.length);
  for (let i = 0; i < float32Array.length; i++) {
    const s = Math.max(-1, Math.min(1, float32Array[i]));
    int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
  }
  return int16Array;
}

export function useMP3Recorder(): UseMP3RecorderReturn {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isConverting, setIsConverting] = useState(false);

  // Refs
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const mp3EncoderRef = useRef<InstanceType<typeof Mp3Encoder> | null>(null);
  const mp3DataRef = useRef<Uint8Array[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);
  const pausedDurationRef = useRef<number>(0);
  const isPausedRef = useRef<boolean>(false);

  // Cleanup function
  const cleanup = useCallback(() => {
    console.log('🎤 MP3 Recorder: Cleaning up...');
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    
    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }
    
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    mp3EncoderRef.current = null;
    mp3DataRef.current = [];
    startTimeRef.current = 0;
    pausedDurationRef.current = 0;
    isPausedRef.current = false;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  const startRecording = useCallback(async () => {
    try {
      setError(null);
      setAudioBlob(null);
      setDuration(0);
      setIsConverting(false);
      mp3DataRef.current = [];

      console.log('🎤 MP3 Recorder: Requesting microphone access...');
      
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100,
        }
      });

      streamRef.current = stream;

      // Create AudioContext
      const audioContext = new AudioContext({ sampleRate: 44100 });
      audioContextRef.current = audioContext;
      
      // Create source from stream
      const source = audioContext.createMediaStreamSource(stream);
      sourceRef.current = source;
      
      // Create script processor for capturing raw PCM data
      // Using 4096 buffer size for balance between latency and performance
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;
      
      // Initialize MP3 encoder (mono, 44100 Hz, 128 kbps)
      const mp3Encoder = new Mp3Encoder(1, 44100, 128);
      mp3EncoderRef.current = mp3Encoder;
      
      console.log('🎤 MP3 Recorder: MP3 encoder initialized');
      
      // Process audio data
      processor.onaudioprocess = (e) => {
        if (isPausedRef.current || !mp3EncoderRef.current) return;
        
        const inputData = e.inputBuffer.getChannelData(0);
        const samples = floatTo16BitPCM(inputData);
        
        const mp3buf = mp3EncoderRef.current.encodeBuffer(samples);
        if (mp3buf.length > 0) {
          mp3DataRef.current.push(new Uint8Array(mp3buf));
        }
      };
      
      // Connect nodes
      source.connect(processor);
      processor.connect(audioContext.destination);

      setIsRecording(true);
      setIsPaused(false);
      isPausedRef.current = false;
      startTimeRef.current = Date.now();

      // Start duration timer
      timerRef.current = setInterval(() => {
        if (!isPausedRef.current) {
          const elapsed = Date.now() - startTimeRef.current + pausedDurationRef.current;
          setDuration(Math.floor(elapsed / 1000));
        }
      }, 100);

      console.log('🎤 MP3 Recorder: Recording started (real MP3 encoding)');
    } catch (err: any) {
      console.error('🎤 MP3 Recorder: Error starting recording:', err);
      
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Permissão de microfone negada. Habilite nas configurações do navegador.');
      } else if (err.name === 'NotFoundError') {
        setError('Nenhum microfone encontrado.');
      } else {
        setError('Erro ao iniciar gravação: ' + (err.message || 'Erro desconhecido'));
      }
      
      cleanup();
      setIsRecording(false);
    }
  }, [cleanup]);

  const stopRecording = useCallback(async (): Promise<Blob | null> => {
    if (!isRecording || !mp3EncoderRef.current) {
      console.log('🎤 MP3 Recorder: No active recording to stop');
      return null;
    }

    console.log('🎤 MP3 Recorder: Stopping recording...');
    
    try {
      // Flush the encoder to get remaining MP3 data
      const mp3buf = mp3EncoderRef.current.flush();
      if (mp3buf.length > 0) {
        mp3DataRef.current.push(new Uint8Array(mp3buf));
      }
      
      // Combine all MP3 chunks into a single blob
      const mp3Blob = new Blob(mp3DataRef.current as BlobPart[], { type: 'audio/mpeg' });
      
      console.log('🎤 MP3 Recorder: Recording complete, size:', mp3Blob.size, 'bytes, type: audio/mpeg');
      
      if (mp3Blob.size === 0) {
        setError('Gravação vazia. Tente novamente.');
        cleanup();
        setIsRecording(false);
        return null;
      }
      
      setAudioBlob(mp3Blob);
      setIsRecording(false);
      setIsPaused(false);
      cleanup();
      
      return mp3Blob;
    } catch (err: any) {
      console.error('🎤 MP3 Recorder: Error processing recording:', err);
      setError('Erro ao processar áudio: ' + (err.message || 'Erro desconhecido'));
      cleanup();
      setIsRecording(false);
      return null;
    }
  }, [isRecording, cleanup]);

  const cancelRecording = useCallback(() => {
    console.log('🎤 MP3 Recorder: Cancelling recording...');
    cleanup();
    setIsRecording(false);
    setIsPaused(false);
    setDuration(0);
    setAudioBlob(null);
    setIsConverting(false);
  }, [cleanup]);

  const pauseRecording = useCallback(() => {
    if (isRecording && !isPaused) {
      isPausedRef.current = true;
      pausedDurationRef.current = Date.now() - startTimeRef.current + pausedDurationRef.current;
      setIsPaused(true);
      console.log('🎤 MP3 Recorder: Recording paused');
    }
  }, [isRecording, isPaused]);

  const resumeRecording = useCallback(() => {
    if (isRecording && isPaused) {
      isPausedRef.current = false;
      startTimeRef.current = Date.now();
      setIsPaused(false);
      console.log('🎤 MP3 Recorder: Recording resumed');
    }
  }, [isRecording, isPaused]);

  return {
    isRecording,
    isPaused,
    duration,
    audioBlob,
    error,
    isConverting,
    startRecording,
    stopRecording,
    cancelRecording,
    pauseRecording,
    resumeRecording,
  };
}
